"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.UnitActivationSchema = exports.IpPlaceSchema = exports.OrderWelcomeLockSchema = exports.RegisterWelcomeSchema = exports.WELCOME_STEP_IDS = void 0;
exports.normalizeUnlockCode = normalizeUnlockCode;
exports.formatUnlockCode = formatUnlockCode;
exports.defaultWelcomeSteps = defaultWelcomeSteps;
exports.resumeStep = resumeStep;
exports.haversineMiles = haversineMiles;
exports.judgeActivationPlace = judgeActivationPlace;
const zod_1 = require("zod");
// "Hello from Ruby" welcome wizard — shared types and pure helpers.
exports.WELCOME_STEP_IDS = ['hello', 'wifi', 'unlock', 'time', 'scanner', 'reader', 'printer', 'display', 'pair', 'ready'];
exports.RegisterWelcomeSchema = zod_1.z.object({
    mode: zod_1.z.enum(['fresh', 'setup']).default('setup'),
    locked: zod_1.z.boolean().default(false),
    orderId: zod_1.z.string().optional(), unitId: zod_1.z.string().optional(),
    lockedBy: zod_1.z.enum(['shipping', 'super_admin']).optional(),
    progress: zod_1.z.record(zod_1.z.object({ status: zod_1.z.enum(['done', 'later']), at: zod_1.z.string() })).default({}),
    openedAt: zod_1.z.string().optional(), unlockedAt: zod_1.z.string().optional(), completedAt: zod_1.z.string().optional(),
    updatedAt: zod_1.z.string().optional(),
    /** Last time the till fetched its state while Fresh or locked — proves it has cached its lock before shipping. */
    deviceSeenAt: zod_1.z.string().optional(),
});
exports.OrderWelcomeLockSchema = zod_1.z.object({
    codeHash: zod_1.z.string(), codeEnc: zod_1.z.string().optional(), issuedAt: zod_1.z.string(),
    emailedAt: zod_1.z.string().optional(), emailedTo: zod_1.z.string().optional(),
    wrongTries: zod_1.z.number().int().default(0), heldAt: zod_1.z.string().optional(),
    shipUnlocked: zod_1.z.boolean().default(false),
});
exports.IpPlaceSchema = zod_1.z.object({
    city: zod_1.z.string().optional(), region: zod_1.z.string().optional(), country: zod_1.z.string().optional(),
    lat: zod_1.z.number().optional(), lon: zod_1.z.number().optional(),
});
exports.UnitActivationSchema = zod_1.z.object({
    at: zod_1.z.string(), ip: zod_1.z.string().optional(), place: exports.IpPlaceSchema.nullable().default(null),
    wifiSsid: zod_1.z.string().optional(), milesFromBusiness: zod_1.z.number().optional(),
    nearShipTo: zod_1.z.boolean().optional(), nearBusiness: zod_1.z.boolean().optional(),
    flagged: zod_1.z.boolean().default(false), via: zod_1.z.enum(['code', 'paired_till', 'super_admin', 'ship_unlocked']),
});
/** Uppercase, strip spaces and dashes. */
function normalizeUnlockCode(input) {
    return String(input ?? '').toUpperCase().replace(/[\s-]+/g, '');
}
/** 'K7M2Q9XA' -> 'K7M2-Q9XA' */
function formatUnlockCode(code) {
    const c = normalizeUnlockCode(code);
    return c.length > 4 ? `${c.slice(0, 4)}-${c.slice(4)}` : c;
}
function defaultWelcomeSteps(input) {
    // The CPad unlocks together with its till.
    if (input.isPayScreen)
        return ['hello', 'ready'];
    const caps = input.capabilities ?? {};
    const has = (l) => l === 'builtin' || l === 'external';
    const steps = ['hello', 'wifi', 'unlock', 'time'];
    if (has(caps.printer))
        steps.push('printer');
    if (/D3/i.test(input.modelName ?? '') || input.formFactor === 'android_dual_screen' || input.formFactor === 'kiosk_dual_screen') {
        steps.push('display');
    }
    if (has(caps.scanner))
        steps.push('scanner');
    if (has(caps.cardReader))
        steps.push('reader');
    steps.push('ready');
    return steps;
}
/** First step still to do; 'later' counts as handled. wifi/unlock are skipped when already satisfied. */
function resumeStep(steps, progress, opts) {
    for (const s of steps) {
        if (progress[s])
            continue;
        if (s === 'unlock' && opts.unlocked)
            continue;
        if (s === 'wifi' && opts.online)
            continue;
        return s;
    }
    return 'ready';
}
function haversineMiles(a, b) {
    const R = 3958.7613;
    const rad = (d) => (d * Math.PI) / 180;
    const dLat = rad(b.lat - a.lat);
    const dLon = rad(b.lon - a.lon);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLon / 2) ** 2;
    return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}
const US_STATES = {
    alabama: 'AL', alaska: 'AK', arizona: 'AZ', arkansas: 'AR', california: 'CA', colorado: 'CO', connecticut: 'CT',
    delaware: 'DE', 'district of columbia': 'DC', florida: 'FL', georgia: 'GA', hawaii: 'HI', idaho: 'ID', illinois: 'IL',
    indiana: 'IN', iowa: 'IA', kansas: 'KS', kentucky: 'KY', louisiana: 'LA', maine: 'ME', maryland: 'MD',
    massachusetts: 'MA', michigan: 'MI', minnesota: 'MN', mississippi: 'MS', missouri: 'MO', montana: 'MT',
    nebraska: 'NE', nevada: 'NV', 'new hampshire': 'NH', 'new jersey': 'NJ', 'new mexico': 'NM', 'new york': 'NY',
    'north carolina': 'NC', 'north dakota': 'ND', ohio: 'OH', oklahoma: 'OK', oregon: 'OR', pennsylvania: 'PA',
    'rhode island': 'RI', 'south carolina': 'SC', 'south dakota': 'SD', tennessee: 'TN', texas: 'TX', utah: 'UT',
    vermont: 'VT', virginia: 'VA', washington: 'WA', 'west virginia': 'WV', wisconsin: 'WI', wyoming: 'WY',
};
function stateKey(v) {
    const t = v.trim().toLowerCase();
    return US_STATES[t] ?? t.toUpperCase();
}
function judgeActivationPlace(place, ref) {
    if (!place)
        return { flagged: false };
    const out = { flagged: false };
    const b = ref.business;
    if (place.lat != null && place.lon != null && b?.lat != null && b?.lon != null) {
        const miles = haversineMiles({ lat: place.lat, lon: place.lon }, { lat: b.lat, lon: b.lon });
        out.milesFromBusiness = Math.round(miles * 10) / 10;
        out.nearBusiness = miles <= ref.flagMiles;
    }
    if (place.region && ref.shipTo?.state) {
        out.nearShipTo = stateKey(place.region) === stateKey(ref.shipTo.state);
    }
    const evaluable = out.nearBusiness !== undefined || out.nearShipTo !== undefined;
    out.flagged = evaluable && out.nearBusiness !== true && out.nearShipTo !== true;
    return out;
}
//# sourceMappingURL=welcome.js.map